<template>
  <b-modal
    id="modal-coupon-usage"
    size="lg"
    hide-footer
    @shown="getCouponOrders"
    @hidden="resetList"
  >
    <template #modal-title>
      استفاده‌کنندگان کد
      <span v-if="coupon" dir="ltr" class="text-primary">{{ coupon.code }}</span>
    </template>

    <b-overlay :show="isLoading">
      <p class="usage-note">
        <feather-icon icon="InfoIcon" size="14" class="mr-50" />
        «مجموع تخفیف» همه‌ی تخفیف‌های سفارش است (کد، پکیج و...). برای جزئیات، روی «جزئیات سفارش» بزنید.
      </p>

      <b-alert v-if="isAccessDenied" show variant="warning" class="p-1">
        شما به گزارش استفاده‌کنندگان این کد دسترسی ندارید.
      </b-alert>
      <b-alert v-else-if="hasError" show variant="danger" class="p-1">
        دریافت فهرست استفاده‌کنندگان انجام نشد.
      </b-alert>

      <p v-if="ordersList && !isAccessDenied && !hasError" class="usage-count">
        {{ ordersList.length }} بار استفاده شده
        <template v-if="coupon && coupon.maxUsageCount">
          از سقف {{ coupon.maxUsageCount }} بار
        </template>
      </p>

      <custom-table v-if="!isAccessDenied && !hasError" :tableColumns="tableColumns" :tableItems="ordersList || []">
        <template #userFullName="data">
          <span class="text-nowrap">{{ data.item.userFullName || "-" }}</span>
          <small v-if="data.item.userMobile" class="d-block text-muted" dir="ltr">
            {{ data.item.userMobile }}
          </small>
        </template>
        <template #sumPrice="data">
          <span class="text-nowrap">{{ formatPrice(details(data.item).sumPrice) }}</span>
        </template>
        <template #discount="data">
          <span class="text-nowrap text-danger">{{ formatPrice(discountOf(data.item)) }}</span>
        </template>
        <template #totalAmount="data">
          <strong class="text-nowrap">{{ formatPrice(data.item.totalAmount) }}</strong>
        </template>
        <template #status="data">
          <b-badge
            v-if="details(data.item).status"
            :variant="statusVariant(details(data.item).status)"
            class="text-nowrap"
          >
            {{ orderStatuses[details(data.item).status] || "-" }}
          </b-badge>
          <span v-else class="text-muted">...</span>
        </template>
        <template #createDate="data">
          <span class="text-nowrap">
            {{ new Date(data.item.createDate).toLocaleDateString("fa-IR") }}
          </span>
        </template>
        <template #actions="data">
          <!-- در تب جدید تا مودال و فهرست کدها از دست نرود -->
          <b-link :to="`/orders/${data.item.orderId}`" target="_blank" class="text-nowrap">
            <feather-icon icon="EyeIcon" size="16" class="mr-25" />
            جزئیات سفارش
          </b-link>
        </template>
      </custom-table>

      <p v-if="ordersList && ordersList.length == 0 && !hasError && !isAccessDenied" class="text-center text-muted my-1">
        هنوز کسی از این کد استفاده نکرده است.
      </p>
    </b-overlay>
  </b-modal>
</template>

<script>
import CustomTable from "@/views/components/Utils/CustomTable.vue";
import { GetCouponOrders } from "@/libs/Api/Coupon";
import { GetASingleOrder } from "@/libs/Api/Order";
import { BModal, BOverlay, BAlert, BLink, BBadge } from "bootstrap-vue";

export default {
  props: {
    coupon: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      isAccessDenied: false,
      ordersList: null,
      // جزئیات هر سفارش از Order/Get (کلید: شماره سفارش)
      orderDetails: {},
      // همان وضعیت‌های فهرست سفارش‌های پنل
      orderStatuses: {
        1: "ثبت شده",
        2: "پرداخت شده",
        3: "ارسال شده",
        4: "تحویل شده",
        5: "کنسل شده",
        6: "پرداخت شده توسط ادمین",
        7: "در انتظار پرداخت",
        8: "لغو شده",
        9: "لغو شده توسط سیستم",
      },
      tableColumns: [
        { key: "orderId", label: "شماره سفارش" },
        { key: "userFullName", label: "کاربر" },
        { key: "sumPrice", label: "قیمت اصلی" },
        { key: "discount", label: "مجموع تخفیف" },
        { key: "totalAmount", label: "مبلغ نهایی" },
        { key: "status", label: "وضعیت" },
        { key: "createDate", label: "تاریخ" },
        { key: "actions", label: "سفارش" },
      ],
    };
  },
  methods: {
    formatPrice(value) {
      if (value === null || value === undefined) return "...";
      return Intl.NumberFormat("en-US").format(value) + " تومان";
    },
    details(order) {
      return this.orderDetails[order.orderId] || {};
    },
    discountOf(order) {
      const sumPrice = this.details(order).sumPrice;
      if (sumPrice === undefined) return undefined;
      return Math.max(0, sumPrice - (order.totalAmount || 0));
    },
    statusVariant(status) {
      if ([2, 3, 4, 6].includes(status)) return "success";
      if ([5, 8, 9].includes(status)) return "danger";
      return "warning";
    },
    fetchOrder(orderId) {
      return new Promise((resolve) => {
        let request = new GetASingleOrder(this);
        request.setRequestParam({ orderId });
        request
          .fetch((response) => resolve(response && response.isSuccess ? response.data : null))
          .catch(() => resolve(null));
      });
    },
    async loadOrderDetails(orders) {
      const results = await Promise.all(orders.map((order) => this.fetchOrder(order.orderId)));
      const map = {};
      orders.forEach((order, index) => {
        if (results[index]) map[order.orderId] = results[index];
      });
      this.orderDetails = map;
    },
    resetList() {
      this.orderDetails = {};
      this.ordersList = null;
      this.hasError = false;
      this.isAccessDenied = false;
    },
    async getCouponOrders() {
      if (!this.coupon) return;
      this.isLoading = true;
      this.hasError = false;
      try {
        let _this = this;
        let getCouponOrders = new GetCouponOrders(_this);
        getCouponOrders.setRequestParam({ couponId: _this.coupon.couponId });
        await getCouponOrders.get(
          (res) => {
            if (res && res.isSuccess && Array.isArray(res.data)) {
              _this.ordersList = res.data;
              _this.loadOrderDetails(res.data);
            } else if (res && res.status == 422) {
              // 422 یعنی نقش کاربر به این گزارش دسترسی ندارد
              _this.isAccessDenied = true;
            } else {
              _this.hasError = true;
            }
          },
          () => {
            _this.hasError = true;
          }
        );
      } catch (error) {
        this.hasError = true;
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  components: {
    CustomTable,
    BModal,
    BOverlay,
    BAlert,
    BLink,
    BBadge,
  },
};
</script>

<style scoped>
.usage-count {
  margin-bottom: 0.75rem;
  font-weight: 600;
}

.usage-note {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
  font-size: 0.8rem;
  color: #6e6b7b;
}
</style>
